import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Users as UsersIcon,
  Plus,
  Trash2,
  Shield,
  ShieldCheck,
  Edit3,
  Eye,
  Mail,
  Loader2,
  KeyRound,
  UploadCloud,
  ChevronDown,
  ChevronRight,
  Folder,
  ListChecks,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { usersApi, clientsApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PendingInvitesSection from "@/components/PendingInvitesSection";

const ROLE_META = {
  admin: { icon: Shield, color: "text-rose-400", label: "Admin" },
  manager: { icon: ShieldCheck, color: "text-sky-400", label: "Manager" },
  editor: { icon: Edit3, color: "text-amber-400", label: "Editor" },
  contributor: { icon: UploadCloud, color: "text-emerald-400", label: "Contributor" },
  viewer: { icon: Eye, color: "text-zinc-400", label: "Viewer" },
};

export default function AdminUsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [assignments, setAssignments] = useState({}); // { userId → {project_count, open_task_count, projects[]} }
  const [clients, setClients] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const isAdmin = me?.role === "admin";
  const clientById = useMemo(() => {
    const m = new Map();
    clients.forEach((c) => m.set(c.id, c));
    return m;
  }, [clients]);

  const load = async () => {
    setLoading(true);
    try {
      // Users list is the primary payload; assignments + clients are
      // enrichment and must never block the page.
      const [userList, assignmentPayload, clientList] = await Promise.all([
        usersApi.list(),
        usersApi.projectAssignments().catch(() => ({ assignments: {} })),
        clientsApi.list().catch(() => []),
      ]);
      setUsers(userList);
      setAssignments(assignmentPayload?.assignments || {});
      setClients(clientList);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (userId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  useEffect(() => {
    // Guard the fetch — non-admins landing on this URL (bookmark, stale tab,
    // manual nav) shouldn't trigger a 403 + toast before the render gate hits.
    // Instead we flip loading off so the "Admin access required" panel shows
    // immediately.
    if (!me) return;
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    load();
  }, [isAdmin, me]);

  const handleRemove = async (id, email) => {
    try {
      await usersApi.remove(id);
      toast.success(`${email} removed`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove user");
    }
  };

  if (me && !isAdmin) {
    return (
      <div className="max-w-md mx-auto text-center py-24 px-6">
        <Shield className="w-12 h-12 mx-auto mb-4 text-zinc-700" />
        <h2 className="font-heading text-2xl font-light text-zinc-100 mb-2">
          Admin access required
        </h2>
        <p className="text-sm text-zinc-500 mb-6">
          Only admins can manage users. Ask a workspace admin if you need to
          change someone&apos;s role.
        </p>
        <Link
          to="/launchpad"
          data-testid="back-to-launchpad-link"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-zinc-800 text-sm text-zinc-300 hover:text-amber-400 hover:border-amber-500/40 transition-colors"
        >
          Back to Launchpad
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-screen-xl mx-auto px-6 lg:px-10 py-8">
      <header className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-overline text-zinc-500">Settings</div>
          <h1 className="font-heading text-3xl text-zinc-100 leading-tight">Users</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Manage who has access. Project membership is set on each project.
          </p>
        </div>
        <Button
          data-testid="new-user-btn"
          onClick={() => { setEditUser(null); setOpen(true); }}
          className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm font-semibold"
        >
          <Plus className="w-4 h-4 mr-1" /> New User
        </Button>
      </header>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
        </div>
      ) : (
        <>
          {/* Pending invites — surfaced above the active users list so admins
              can spot & resurrect stale invites without hunting. Silent when
              the list is empty. */}
          <PendingInvitesSection />
          <div className="border border-zinc-800 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60">
              <tr>
                <th className="text-left p-3 text-overline text-zinc-500">Name</th>
                <th className="text-left p-3 text-overline text-zinc-500">Email</th>
                <th className="text-left p-3 text-overline text-zinc-500">Role</th>
                <th className="text-left p-3 text-overline text-zinc-500">Client</th>
                <th className="text-left p-3 text-overline text-zinc-500">Active Projects</th>
                <th className="text-right p-3 text-overline text-zinc-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const meta = ROLE_META[u.role] || ROLE_META.viewer;
                const Icon = meta.icon;
                const isSelf = me && u.id === me.id;
                const assignment = assignments[u.id];
                const isExpanded = expanded.has(u.id);
                return (
                  <React.Fragment key={u.id}>
                  <tr
                    data-testid={`user-row-${u.id}`}
                    className="border-t border-zinc-800/60 hover:bg-zinc-900/40"
                  >
                    <td className="p-3 text-zinc-100">
                      {u.name || "—"}
                      {isSelf && <span className="ml-2 text-xs text-amber-500">(you)</span>}
                    </td>
                    <td className="p-3 text-zinc-400 font-mono text-xs">{u.email}</td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs ${meta.color}`}>
                        <Icon className="w-3.5 h-3.5" />
                        {meta.label}
                      </span>
                    </td>
                    <td className="p-3 align-top">
                      {u.client_id ? (
                        <Link
                          to={`/clients/${u.client_id}`}
                          data-testid={`user-client-link-${u.id}`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-mono uppercase tracking-wider border border-amber-500/40 text-amber-300 bg-amber-500/10 hover:bg-amber-500/20"
                        >
                          {clientById.get(u.client_id)?.name || "Client"}
                        </Link>
                      ) : (
                        <span
                          data-testid={`user-client-empty-${u.id}`}
                          className="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-mono uppercase tracking-wider border border-zinc-800 text-zinc-500"
                        >
                          Studio
                        </span>
                      )}
                    </td>
                    <td className="p-3 align-top">
                      <UserProjectsCell
                        userId={u.id}
                        assignment={assignment}
                        expanded={isExpanded}
                        onToggle={() => toggleExpanded(u.id)}
                      />
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          data-testid={`edit-user-${u.id}`}
                          onClick={() => { setEditUser(u); setOpen(true); }}
                          className="p-1.5 rounded-sm text-zinc-500 hover:text-amber-500 hover:bg-zinc-900"
                          title="Edit user"
                        >
                          <KeyRound className="w-3.5 h-3.5" />
                        </button>
                        {!isSelf && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button
                                data-testid={`delete-user-${u.id}`}
                                className="p-1.5 rounded-sm text-zinc-500 hover:text-rose-400 hover:bg-zinc-900"
                                title="Delete user"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remove {u.email}?</AlertDialogTitle>
                                <AlertDialogDescription className="text-zinc-500">
                                  This permanently removes the user account and all their project memberships.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900">
                                  Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleRemove(u.id, u.email)}
                                  className="bg-rose-600 hover:bg-rose-500 text-white"
                                >
                                  Remove
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </td>
                  </tr>
                  {isExpanded && assignment?.projects?.length > 0 && (
                    <tr
                      data-testid={`user-projects-expanded-${u.id}`}
                      className="bg-zinc-950/60"
                    >
                      <td colSpan={6} className="p-4 pl-8 border-t border-zinc-900">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {assignment.projects.map((p) => (
                            <ProjectAssignmentTile key={p.id} project={p} />
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
              {users.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-10 text-center text-zinc-500">
                    <UsersIcon className="w-10 h-10 mx-auto mb-3 opacity-30" />
                    No users yet. Add your first one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </>
      )}

      <UserDialog
        open={open}
        onOpenChange={setOpen}
        user={editUser}
        onSaved={load}
        clients={clients}
      />
    </div>
  );
}

function UserDialog({ open, onOpenChange, user, onSaved, clients = [] }) {
  const isEdit = !!user;
  const [form, setForm] = useState({
    email: "",
    name: "",
    role: "editor",
    password: "",
    sendInvite: true,
    client_id: "",  // "" = studio/internal user; else linked client id
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        email: user?.email || "",
        name: user?.name || "",
        role: user?.role || "editor",
        password: "",
        sendInvite: !user, // new users default to invite
        client_id: user?.client_id || "",
      });
    }
  }, [open, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isEdit) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        // Only send client_id when it changed. Empty string is a
        // meaningful signal (unlink) — distinguish from "leave alone".
        const currentClient = user?.client_id || "";
        if (form.client_id !== currentClient) {
          payload.client_id = form.client_id || "";
        }
        await usersApi.update(user.id, payload);
        toast.success("User updated");
      } else {
        const payload = {
          email: form.email.trim().toLowerCase(),
          name: form.name,
          role: form.role,
        };
        if (form.client_id) payload.client_id = form.client_id;
        if (!form.sendInvite) payload.password = form.password;
        const res = await usersApi.create(payload);
        toast.success(res.invited ? `Invitation sent to ${payload.email}` : `User ${payload.email} created`);
      }
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">
            {isEdit ? "Edit user" : "Add a new user"}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            {isEdit
              ? "Update role, name, or set a new password."
              : "Send an email invitation, or create the account directly with a password."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          {!isEdit && (
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">Email</Label>
              <Input
                data-testid="user-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">Name</Label>
            <Input
              data-testid="user-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={isEdit ? "" : "Optional"}
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">
              Client link <span className="text-zinc-600 normal-case">(optional)</span>
            </Label>
            <select
              data-testid="user-client-select"
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              className="w-full appearance-none px-3 py-2 text-sm rounded-md bg-zinc-950 border border-zinc-800 hover:border-zinc-700 focus:border-amber-500 focus:outline-none text-zinc-100"
            >
              <option value="">Studio / internal user</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-zinc-600 leading-tight">
              Client-linked users only see their own client&apos;s projects.
              Notes, other clients, Studio, Team, and admin surfaces are hidden.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="user-role-select" className="bg-zinc-950 border-zinc-800">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                <SelectItem value="admin">Admin — full access</SelectItem>
                <SelectItem value="manager">
                  Manager — editor + team-roster Launchpad
                </SelectItem>
                <SelectItem value="editor">Editor — invited per-project</SelectItem>
                <SelectItem value="contributor">
                  Contributor — invited per-project, uploads files &amp; videos only
                </SelectItem>
                <SelectItem value="viewer">Viewer — invited per-project, read-only</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-zinc-500">
              {`Managers, Editors, and Viewers only see projects they're invited to. Managers get a team-roster view on the Launchpad.`}
            </p>
          </div>
          {!isEdit && (
            <div className="space-y-2 pt-1">
              <label className="flex items-center gap-2 text-xs text-zinc-300">
                <input
                  data-testid="user-send-invite-checkbox"
                  type="checkbox"
                  checked={form.sendInvite}
                  onChange={(e) => setForm({ ...form, sendInvite: e.target.checked })}
                  className="accent-amber-500"
                />
                <Mail className="w-3.5 h-3.5" /> Send an email invitation (user sets their own password)
              </label>
              {!form.sendInvite && (
                <Input
                  data-testid="user-password-input"
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Temporary password (min 8 chars)"
                  className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
                />
              )}
            </div>
          )}
          {isEdit && (
            <div className="space-y-1.5 pt-1">
              <Label className="text-overline text-zinc-500">Set new password (optional)</Label>
              <Input
                data-testid="user-edit-password-input"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Leave blank to keep current"
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
              />
            </div>
          )}
          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="user-save-btn"
              className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm"
            >
              {submitting ? "Saving…" : isEdit ? "Save" : "Add user"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Compact per-user cell in the users table.
//
// Layout: [N proj · M tasks]  [Chip 1] [Chip 2] [+N ▾]
// - Always shows the numeric badge so admins can compare workloads at a
//   glance without expanding anything.
// - Up to 2 project chips render inline (color-coded by cover_color) so
//   the common case (small assignment list) needs no interaction.
// - When there are more than 2 projects, the "+N" chevron toggles the
//   parent row's expanded state, revealing every project as a tile with
//   role + open-task count in a second row below.
const COVER_CHIP_CLASSES = {
  amber:   "bg-amber-500/15 border-amber-500/40 text-amber-300",
  rose:    "bg-rose-500/15 border-rose-500/40 text-rose-300",
  emerald: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300",
  blue:    "bg-blue-500/15 border-blue-500/40 text-blue-300",
  violet:  "bg-violet-500/15 border-violet-500/40 text-violet-300",
  cyan:    "bg-cyan-500/15 border-cyan-500/40 text-cyan-300",
  fuchsia: "bg-fuchsia-500/15 border-fuchsia-500/40 text-fuchsia-300",
  sky:     "bg-sky-500/15 border-sky-500/40 text-sky-300",
  slate:   "bg-slate-500/15 border-slate-500/40 text-slate-300",
};

function UserProjectsCell({ userId, assignment, expanded, onToggle }) {
  if (!assignment || !assignment.projects?.length) {
    return (
      <span
        data-testid={`user-projects-empty-${userId}`}
        className="text-xs text-zinc-600 italic"
      >
        No active projects
      </span>
    );
  }
  const { project_count: projectCount, open_task_count: openTaskCount, projects } = assignment;
  const inline = projects.slice(0, 2);
  const hidden = Math.max(0, projects.length - inline.length);
  return (
    <div
      className="flex flex-col gap-2"
      data-testid={`user-projects-cell-${userId}`}
    >
      <div className="flex items-center gap-1.5 text-[11px] font-mono text-zinc-400">
        <Folder className="w-3 h-3 text-zinc-500" />
        <span data-testid={`user-projects-count-${userId}`}>
          {projectCount} proj
        </span>
        <span className="text-zinc-700">·</span>
        <ListChecks className="w-3 h-3 text-zinc-500" />
        <span data-testid={`user-tasks-count-${userId}`}>
          {openTaskCount} {openTaskCount === 1 ? "task" : "tasks"}
        </span>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {inline.map((p) => (
          <Link
            key={p.id}
            to={`/projects/${p.id}`}
            data-testid={`user-project-chip-${userId}-${p.id}`}
            title={`${p.name}${p.membership_role ? ` · ${p.membership_role}` : ""}${p.open_task_count ? ` · ${p.open_task_count} open` : ""}`}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-mono border max-w-[160px] hover:brightness-125 transition ${COVER_CHIP_CLASSES[p.cover_color] || COVER_CHIP_CLASSES.amber}`}
          >
            <span className="truncate">{p.name || "Untitled"}</span>
            {p.open_task_count > 0 && (
              <span className="opacity-70">· {p.open_task_count}</span>
            )}
          </Link>
        ))}
        {(hidden > 0 || projects.length > 0) && (
          <button
            type="button"
            data-testid={`user-projects-toggle-${userId}`}
            onClick={onToggle}
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[11px] font-mono border border-zinc-800 text-zinc-400 hover:text-amber-400 hover:border-amber-500/40"
            title={expanded ? "Hide project list" : "Show all projects"}
          >
            {hidden > 0 && !expanded && <span>+{hidden}</span>}
            {expanded ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )}
          </button>
        )}
      </div>
    </div>
  );
}

// Tile shown in the expanded row (one per project the user touches).
function ProjectAssignmentTile({ project }) {
  const chipCls = COVER_CHIP_CLASSES[project.cover_color] || COVER_CHIP_CLASSES.amber;
  return (
    <Link
      to={`/projects/${project.id}`}
      data-testid={`project-tile-${project.id}`}
      className="flex items-center gap-2 px-3 py-2 rounded-sm border border-zinc-800 bg-zinc-950/60 hover:border-amber-500/40 hover:bg-zinc-900/60 transition"
    >
      <span className={`w-1.5 h-1.5 rounded-full ${chipCls} border-0`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-zinc-100 truncate">
          {project.name || "Untitled"}
        </div>
        <div className="text-[10px] font-mono text-zinc-500 mt-0.5 uppercase tracking-wide">
          {project.membership_role || "task-only"}
          {project.open_task_count > 0 && (
            <span className="ml-2 text-amber-400">
              {project.open_task_count} open
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

